import {ChangeDetectionStrategy, Component, model, output, signal} from '@angular/core';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSelectModule} from '@angular/material/select';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {fromEvent} from "rxjs";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {TranslateModule} from "@ngx-translate/core";
import {MikrofonPulsKomponent} from "web-ng2/delt/komponenter/mikrofon-puls/mikrofon-puls";
import {MatDivider} from "@angular/material/list";

const debug = initDebugger('MikrofonVelgerKomponent');

export interface Mikrofon {
    deviceId: string;
    /** Navnet nettleseren oppgir. Er tomt inntil brukeren har gitt mikrofontilgang */
    label: string;
}

/**
 * Nedtrekksliste for valg av mikrofon, med knapp for å hente enhetene på nytt.
 * Lista holdes oppdatert når mikrofoner kobles til eller fra.
 */
@Component({
    selector: 'mikrofon-velger',
    templateUrl: 'mikrofon-velger.html',
    styleUrl: 'mikrofon-velger.scss',
    imports: [
        MatFormFieldModule,
        MatSelectModule,
        MatButtonModule,
        MatIconModule,
        TranslateModule,
        MikrofonPulsKomponent,
        MatDivider
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MikrofonVelgerKomponent {
    /** deviceId til valgt mikrofon, eller null for standardmikrofonen */
    valgtDeviceId = model<string | null>(null);
    vedFeil = output<Error>();

    enheter = signal<Mikrofon[]>([]);
    mikrofonPulsFeil = signal<string | undefined>(undefined);


    constructor() {
        if (erNil(navigator.mediaDevices?.enumerateDevices)) return;

        if (erIkkeNil(navigator.mediaDevices.ondevicechange))
            fromEvent(navigator.mediaDevices, 'devicechange')
              .pipe(takeUntilDestroyed())
              .subscribe(() => this.oppdaterEnheter(false));

        void this.oppdaterEnheter(false);
    }

    /**
     * Henter mikrofonene på nytt.
     *
     * Nettleseren oppgir navn på enhetene først når brukeren har gitt mikrofontilgang. `beOmTilgang`
     * ber derfor om en kortvarig tilgang, og er det knappen i lista bruker. Den som allerede har
     * tilgang – feks fordi et opptak er i gang – kan kalle med `false` for å slippe en ny forespørsel.
     */
    async oppdaterEnheter(beOmTilgang = true): Promise<void> {
        if (erNil(navigator.mediaDevices?.enumerateDevices)) return;

        try {
            if (beOmTilgang) {
                //kortvarig tilgang kun for å få navnene på enhetene
                const lydstrøm = await navigator.mediaDevices.getUserMedia({audio: true});
                lydstrøm.getTracks().forEach(spor => spor.stop());
            }

            const enheter = await navigator.mediaDevices.enumerateDevices();
            this.enheter.set(enheter
              .filter(enhet => enhet.kind === 'audioinput')
              .map(enhet => ({deviceId: enhet.deviceId, label: enhet.label})));
            debug(`fant ${this.enheter().length} mikrofoner`);

            this.nullstillValgSomIkkeFinnes();
        } catch (feil: any) {
            debug('kunne ikke hente mikrofoner', feil);
            this.vedFeil.emit(feil instanceof Error ? feil : new Error(String(feil)));
        }
    }

    vedMikrofonPulsFeil(feil: Error): void {
        this.mikrofonPulsFeil.set(feil.message);
    }

    private nullstillValgSomIkkeFinnes(): void {
        const valgt = this.valgtDeviceId();
        if (erIkkeNil(valgt) && !this.enheter().some(enhet => enhet.deviceId === valgt))
            this.valgtDeviceId.set(null);
    }
}
